import { useMemo, useState } from 'react';
import type { Period } from '@tariff/periods';
import { validatePeriod } from '@tariff/periods';
import { useT, type MessageKey } from '../lib/i18n';

/**
 * The shared period library.
 *
 * Nearly every accommodation prices the same calendar, so today those dates get
 * retyped per rate, per accommodation, per year — with the obvious failure mode
 * that one of them is wrong and nothing says so.
 *
 * ---------------------------------------------------------------------------
 * TWO WINDOWS PER PERIOD
 * ---------------------------------------------------------------------------
 *
 * A period carries the holiday itself, and optionally a wider window that
 * discounts belonging to it may be placed in. That is not a quirk; it is how
 * the park has always priced. Ascension 2026 is five nights for day rates
 * (13-18 May) while arrangements carrying its discount run over eighteen
 * (4-22 May), reaching into the cheaper weeks either side, so a fortnight-long
 * arrangement stays usable when a night or two falls outside the holiday.
 *
 * The wide window is safe in one direction only, which is why the generator
 * checks it: reaching into CHEAPER weeks costs nothing, because a discount
 * cannot win where the nightly rates are already lower. Reaching into a DEARER
 * period undercuts it.
 */

export interface PeriodsProps {
  periods: Period[];
  year: number;
  onYear: (year: number) => void;
  onSave: (p: Period) => void;
  onDelete: (id: string) => void;
  onSeed: (year: number) => void;
  busy?: boolean;
}

const KINDS: Array<Period['kind']> = ['publicHoliday', 'schoolHoliday', 'season', 'parkPeriod'];

/**
 * The kinds, in words.
 *
 * A closed set from the schema, so unlike a period's LABEL — which the operator
 * typed and which stays exactly as they typed it — this is ours to translate.
 */
const KIND_LABEL: Record<Period['kind'], MessageKey> = {
  publicHoliday: 'periods.kindPublicHoliday',
  schoolHoliday: 'periods.kindSchoolHoliday',
  season: 'periods.kindSeason',
  parkPeriod: 'periods.kindParkPeriod',
};

export function Periods({ periods, year, onYear, onSave, onDelete, onSeed, busy }: PeriodsProps) {
  const t = useT();
  const [editing, setEditing] = useState<Period | null>(null);

  const forYear = useMemo(
    () => periods.filter((p) => p.year === year)
      .sort((a, b) => a.from.localeCompare(b.from) || a.label.localeCompare(b.label)),
    [periods, year],
  );

  const blank = (): Period => ({
    id: `period-${year}-${Date.now().toString(36)}`,
    label: '',
    kind: 'season',
    year,
    from: `${year}-04-01`,
    until: `${year}-04-08`,
    source: 'manual',
  });

  return (
    <div className="stack">
      <section className="panel">
        <div className="panel-hd">
          <h2>{t('periods.heading')}</h2>
          <span className="grow" />
          <div className="field" style={{ maxWidth: 110 }}>
            <label htmlFor="pyear" className="sr">{t('app.season')}</label>
            <input
              id="pyear" type="number" value={year}
              onChange={(e) => onYear(Number(e.target.value) || year)}
            />
          </div>
          <button type="button" className="btn" onClick={() => onSeed(year)} disabled={busy}>
            {t('periods.seedDutchHolidays')}
          </button>
          <button type="button" className="btn pri" onClick={() => setEditing(blank())}>
            {t('periods.newPeriod')}
          </button>
        </div>
        <div className="panel-bd">
          <p className="note" style={{ marginBottom: 12 }}>{t('periods.intro')}</p>
          {forYear.length === 0 ? (
            <p className="note">{t('periods.emptyYear', { year })}</p>
          ) : (
            <div className="scroll-x">
              <table>
                <thead>
                  <tr>
                    <th>{t('periods.columnPeriod')}</th>
                    <th>{t('periods.columnHoliday')}</th>
                    <th>{t('periods.columnDiscounts')}</th>
                    <th>{t('periods.columnKind')}</th>
                    <th>{t('periods.columnSource')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {forYear.map((p) => (
                    <tr key={p.id}>
                      <td>
                        {p.label}
                        {p.note && <div className="meta">{p.note}</div>}
                      </td>
                      <td className="num">{p.from} → {p.until}</td>
                      <td className="num">
                        {p.applies
                          ? <>{p.applies.from} → {p.applies.until}</>
                          : <span className="muted">{t('periods.sameAsHoliday')}</span>}
                      </td>
                      <td><span className="chip">{t(KIND_LABEL[p.kind])}</span></td>
                      <td>
                        {/* The source is free text from whatever imported the
                            period — `park-history`, a filename — so it is data
                            and stays as written. */}
                        <span className="chip">{p.source}</span>
                        {p.usage && (
                          <span className="meta"> {t('periods.usedBy', { count: p.usage.used })}</span>
                        )}
                      </td>
                      <td className="r">
                        <button type="button" className="btn" onClick={() => setEditing(p)}>
                          {t('periods.edit')}
                        </button>
                        {' '}
                        <button
                          type="button" className="btn danger"
                          onClick={() => onDelete(p.id)} disabled={busy}
                        >
                          {t('periods.delete')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {editing && (
        <PeriodForm
          period={editing}
          onCancel={() => setEditing(null)}
          onSubmit={(p) => { onSave(p); setEditing(null); }}
        />
      )}
    </div>
  );
}

function PeriodForm({ period, onSubmit, onCancel }: {
  period: Period;
  onSubmit: (p: Period) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const [draft, setDraft] = useState<Period>(period);
  const [wide, setWide] = useState(Boolean(period.applies));

  const value: Period = wide
    ? { ...draft, applies: draft.applies ?? { from: draft.from, until: draft.until } }
    : { ...draft, applies: undefined };

  const problems = validatePeriod(value);

  const set = (patch: Partial<Period>) => setDraft((d) => ({ ...d, ...patch }));
  const setApplies = (patch: Partial<{ from: string; until: string }>) =>
    setDraft((d) => ({
      ...d,
      applies: { from: d.applies?.from ?? d.from, until: d.applies?.until ?? d.until, ...patch },
    }));

  return (
    <section className="panel">
      <div className="panel-hd">
        <h2>
          {period.label
            ? t('periods.editHeading', { label: period.label })
            : t('periods.newPeriod')}
        </h2>
      </div>
      <div className="panel-bd">
        <div className="controls">
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label htmlFor="plabel">{t('periods.name')}</label>
            {/* The placeholder is an EXAMPLE of a period name, so it is ours to
                write and belongs in the catalogue — a Dutch operator should be
                prompted with a Dutch holiday and an English one with a holiday
                they recognise. What they type over it is data. */}
            <input
              id="plabel" value={draft.label} placeholder={t('periods.namePlaceholder')}
              onChange={(e) => set({ label: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="pkind">{t('periods.kind')}</label>
            <select
              id="pkind" value={draft.kind}
              onChange={(e) => set({ kind: e.target.value as Period['kind'] })}
            >
              {KINDS.map((k) => <option key={k} value={k}>{t(KIND_LABEL[k])}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="pfrom">{t('periods.holidayFrom')}</label>
            <input
              id="pfrom" type="date" value={draft.from}
              onChange={(e) => set({ from: e.target.value, year: Number(e.target.value.slice(0, 4)) })}
            />
          </div>
          <div className="field">
            <label htmlFor="puntil">{t('periods.untilExclusive')}</label>
            <input
              id="puntil" type="date" value={draft.until}
              onChange={(e) => set({ until: e.target.value })}
            />
          </div>
        </div>

        <label className="check" style={{ marginTop: 14 }}>
          <input type="checkbox" checked={wide} onChange={(e) => setWide(e.target.checked)} />
          {t('periods.wideWindow')}
        </label>

        {wide && (
          <>
            <div className="controls" style={{ marginTop: 10 }}>
              <div className="field">
                <label htmlFor="pafrom">{t('periods.discountsFrom')}</label>
                <input
                  id="pafrom" type="date" value={value.applies?.from ?? ''}
                  onChange={(e) => setApplies({ from: e.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="pauntil">{t('periods.untilExclusive')}</label>
                <input
                  id="pauntil" type="date" value={value.applies?.until ?? ''}
                  onChange={(e) => setApplies({ until: e.target.value })}
                />
              </div>
            </div>
            {/* A wide window is safe downwards because a discount can never
                win where the nightly rates are already lower; only reaching
                into a dearer period actually undercuts it. */}
            <p className="note" style={{ marginTop: 8 }}>{t('periods.wideWindowNote')}</p>
          </>
        )}

        {problems.length > 0 && (
          <div className="diag" style={{ marginTop: 12 }}>
            {problems.map((p) => (
              <div className="dg error" key={p.code}>
                <span className="ic" aria-hidden="true">✕</span>
                <span>{p.message}</span>
              </div>
            ))}
          </div>
        )}

        <div className="row" style={{ marginTop: 14 }}>
          <button
            type="button" className="btn pri"
            disabled={problems.length > 0 || !draft.label.trim()}
            onClick={() => onSubmit(value)}
          >
            {t('periods.save')}
          </button>
          <button type="button" className="btn" onClick={onCancel}>{t('periods.cancel')}</button>
        </div>
      </div>
    </section>
  );
}
